import { useDispatch, useSelector } from 'react-redux'
import { Post, Posts, PostEventData, Attachment } from './posts.types'


import createLogger from 'debug'
import useSWR, { ConfigInterface, responseInterface } from 'swr'
import { useApi } from '../lib/useApi'
import { swrLoadOnce } from '../lib/swrConfig'
import { Circle } from 'circle/circle.types'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { updatePostAction, updateResponsesAction } from 'events/selectors.event'
import API from 'lib/API'
import { Dispatch } from 'redux'

import { extractResponsesData, ResponsesResult, CirclePostsData } from 'lib-core'

import { useCreation } from "ahooks"
import { EventData } from './EventData'

// to enable localStorage.debug = 'info:*'
const info = createLogger('info:ui')

export const selectPost = (state): Post => {
    return state.post.value
}

export const selectAttachments = (state): Record<string, Attachment> => {
    return state.attachments
}

export const useAttachments = () => {
    return useSelector(selectAttachments)
}

export const usePostLoaded = () => {
    return useSelector(selectPost)
}
export const useResponses = (): ResponsesResult => {
    const raw = useSelector((s: any) => s.responses)
    return useCreation(() => extractResponsesData(raw), [raw])
}

export const useRefreshPostResponses = () => {
    const post = usePostLoaded()
    return useRefreshResponses(post?._id)
}

export const useRefreshResponses = (postId: string) => {
    const ref = useRef<boolean>()
    useEffect(() => () => ref.current = true, [])

    const api = useApi()
    const dispatch = useDispatch()
    return useCallback(async () => {
        if (!postId) {
            return
        }

        const opts = { _id: postId }
        const result = await api.loadResponses(opts)

        if (ref.current) {
            return
        }

        dispatch(updateResponsesAction(result, opts))
    }, [postId])
}

export const usePost = (postId: string, config?: Partial<ConfigInterface<Post, any>>) => {
    const api = useApi()
    const result = useSWR<Post>(postId && `posts/${postId}`, (path) => api.makeRequest({
        path,
        method: "GET"
    }), config ? { ...swrLoadOnce, ...config } : swrLoadOnce)

    if (!postId) {
        return { data: config?.initialData }
    }

    const { data } = result

    if (data) {
        info("post: %o", data)
    }

    return result
}

export const updatePostRequest = async ({ api, dispatch, postId }: { api: API, dispatch: Dispatch, postId: string }) => {
    const result = await api.loadPostById({
        _id: postId,
    })
    dispatch(updatePostAction(result))
    return result
}

export const usePostRefresh = (postId: string) => {
    const ref = useRef<boolean>()
    useEffect(() => () => ref.current = true, [])

    const api = useApi()
    const dispatch = useDispatch()

    return async () => {
        if (!postId) {
            return
        }
        const result = await api.loadPostById({ _id: postId })

        if (ref.current) {
            return
        }
        dispatch(updatePostAction(result))
    }
}

export const useUpdatePostTimer = (postId: string) => {
    const api = useApi()
    const dispatch = useDispatch()

    useEffect(() => {
        if (!postId) {
            return
        }

        const refresh = async () => updatePostRequest({ api, dispatch, postId })

        const handle = setInterval(refresh, 60 * 1000)
        return () => clearInterval(handle)
    }, [postId])
}

export const useCircle = (groupId: string) => {
    const api = useApi()
    const result = useSWR<Circle>(groupId && `groups/${groupId}`, (path) => api.makeRequest({
        path,
        method: "GET"
    }), swrLoadOnce)

    const { data } = result

    if (data) {
        info("group: %o", data)
    }

    return result
}

export const normalizedPostZoomData = (post: Post): Post => {
    if (!post.zoom) {
        return post
    }

    if (typeof post.zoom !== "string") {
        post.zoom.post = post
        return post
    }

    const parsed = JSON.parse(post.zoom)

    return {
        ...post,
        zoom: {
            ...parsed,
            startTime: parseInt(parsed.starttime),
            endTime: parseInt(parsed.endTime),
            post
        }
    }
}

export const usePostZoomMeeting = (postId: string): responseInterface<PostEventData, any> => {
    const result = usePost(postId)
    if (result.data) {
        const post = result.data
        return { ...result, data: new EventData(post) } as any
    }

    return result as any
}

/********************************* 
 * 
 * group
 * 
*/
export const selectDraft = state => state.draft
export const selectDraftValue = (state): Post => state.draft.value
export const useDraft = () => useSelector(selectDraft)
export const useDraftValue = () => useSelector(selectDraftValue)
export const selectPosts = ({ posts }): Posts => posts
export const usePosts = (): Posts => useSelector(selectPosts)

export const useInitialCirclePosts = (): CirclePostsData => {
    const posts = usePosts()
    const { map: content, list } = posts
  
    const { loading, error, values: order } = list || {}
    return {
        order, 
        content
    }
}